import React from 'react';

export type ContactMethodType = 'Email' | 'Phone' | 'Portal';

interface ContactMethodBadgeProps {
  method: ContactMethodType;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'default' | 'outlined' | 'solid';
  className?: string;
}

export const ContactMethodBadge: React.FC<ContactMethodBadgeProps> = ({
  method,
  size = 'md',
  variant = 'default',
  className = '',
}) => {
  const getMethodStyles = () => {
    const baseStyles = {
      Email: {
        bg: 'bg-blue-100',
        text: 'text-blue-700',
        border: 'border-blue-200',
        solid: 'bg-blue-500',
      },
      Phone: {
        bg: 'bg-purple-100',
        text: 'text-purple-700',
        border: 'border-purple-200',
        solid: 'bg-purple-500',
      },
      Portal: {
        bg: 'bg-teal-100',
        text: 'text-teal-700',
        border: 'border-teal-200',
        solid: 'bg-teal-500',
      },
    };

    return baseStyles[method] || baseStyles.Email;
  };

  const getSizeStyles = () => {
    switch (size) {
      case 'sm':
        return 'px-2 py-0.5 text-xs';
      case 'lg':
        return 'px-4 py-2 text-base';
      default:
        return 'px-3 py-1 text-sm';
    }
  };

  const getVariantStyles = () => {
    const colors = getMethodStyles();
    switch (variant) {
      case 'outlined':
        return `bg-transparent ${colors.text} border-2 ${colors.border}`;
      case 'solid':
        return `${colors.solid} text-white border-transparent`;
      default:
        return `${colors.bg} ${colors.text} border ${colors.border}`;
    }
  };

  return (
    <span
      className={`inline-flex items-center rounded-full font-medium ${getSizeStyles()} ${getVariantStyles()} transition-all duration-200 hover:scale-105 ${className}`}
    >
      {method}
    </span>
  );
};

export default function ContactMethodBadgeDemo() {
  const contactMethods: ContactMethodType[] = ['Email', 'Phone', 'Portal'];

  return (
    <div className="p-8 bg-gray-50 min-h-screen">
      <h1 className="text-3xl font-bold mb-8 text-gray-800">Contact Method Badge Component</h1>

      <div className="space-y-8">
        <div className="bg-white p-6 rounded-lg shadow-sm">
          <h2 className="text-xl font-semibold mb-4 text-gray-700">All Contact Methods (Default)</h2>
          <div className="flex flex-wrap gap-3">
            {contactMethods.map(method => (
              <ContactMethodBadge key={method} method={method} />
            ))}
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm">
          <h2 className="text-xl font-semibold mb-4 text-gray-700">Outlined Variant</h2>
          <div className="flex flex-wrap gap-3">
            {contactMethods.map(method => (
              <ContactMethodBadge key={method} method={method} variant="outlined" />
            ))}
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm">
          <h2 className="text-xl font-semibold mb-4 text-gray-700">Solid Variant</h2>
          <div className="flex flex-wrap gap-3">
            {contactMethods.map(method => (
              <ContactMethodBadge key={method} method={method} variant="solid" />
            ))}
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm">
          <h2 className="text-xl font-semibold mb-4 text-gray-700">Size Variations</h2>
          <div className="flex items-center gap-4">
            <ContactMethodBadge method="Email" size="sm" />
            <ContactMethodBadge method="Email" size="md" />
            <ContactMethodBadge method="Email" size="lg" />
          </div>
        </div>
      </div>
    </div>
  );
}
